import * as React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { SwipeableProps, useSwipeable } from 'react-swipeable';
import classNames from 'styles/utils/classNames';

export interface ModalProps {
  isOpen: boolean;
  handleClose: (value?: boolean) => void;
  classNameRounded?: string;
  classNameMaxWidth?: string;
  classNamePosition?: string;
  classNameHeight?: string;
  className?: string;
  positionBottomDesktop?: boolean;
  swipeProps?: SwipeableProps;
  appear?: boolean;
  children?: React.ReactNode;
}

export const EXIT_DURATION_MS = 200;
export const EXIT_DURATION_SAFE_MS = EXIT_DURATION_MS + 300;

// NOTE: How to handle widths and max widths??
export default function Modal({
  isOpen,
  handleClose,
  classNameRounded,
  classNameMaxWidth,
  classNamePosition,
  classNameHeight,
  className,
  swipeProps,
  positionBottomDesktop,
  appear,
  children,
}: ModalProps) {
  const swipeHandlers = swipeProps ? useSwipeable(swipeProps) : {};

  // Freeze children when the modal closes so content doesn't jump during the
  // 200ms exit animation. Headless UI animates the CSS but re-renders children
  // immediately with whatever new props the parent passes (e.g. nulled-out data).
  // The snapshot stays current while open, freezes on close, and is discarded
  // when Headless UI unmounts the tree after the transition completes.
  const childrenSnapshot = React.useRef(children);
  if (isOpen) {
    childrenSnapshot.current = children;
  }

  return (
    <Transition.Root appear={appear} show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-800 bg-opacity-30 transition-opacity" />
        </Transition.Child>

        <div {...swipeHandlers} className="fixed inset-0 z-10 overflow-y-auto">
          <div
            className={classNames(
              'flex min-h-full items-end justify-center',
              positionBottomDesktop ? 'sm:items-end' : 'sm:items-center',
              !!classNameHeight && classNameHeight,
            )}
          >
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div
                className={classNames(
                  'flex w-full items-center justify-center',
                  !!classNameHeight && classNameHeight,
                )}
              >
                <Dialog.Panel
                  className={classNames(
                    'w-full transform overflow-hidden bg-color-bg-lightmode-primary shadow-xl transition-all dark:bg-color-bg-darkmode-primary sm:my-4 sm:w-full',
                    classNameRounded ? classNameRounded : 'rounded-t-3xl sm:rounded-2xl',
                    classNamePosition ? classNamePosition : 'relative',
                    classNameMaxWidth ? classNameMaxWidth : 'sm:max-w-md',
                    !!className && className,
                  )}
                >
                  {childrenSnapshot.current}
                  <div className="safearea-spacer-bot w-full"></div>
                </Dialog.Panel>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
